import { IResultadosBusca } from "../interfaces/IResultadoBusca";
import IComponente, { ICategoria, ITag, ITecnologia } from "../interfaces/IComponente";
import ICategoriaDocumentacao from "../interfaces/ICategoriaDocumentacao";
import ILogComponente from "../interfaces/ILogComponente";
import IDestaque from "../interfaces/IDestaque";
import IMaisAcessado from "../interfaces/IMaisAcessado";
import IRegistros from "../interfaces/IRegistros";
import { IRegistrosArtigosCategoria } from "../interfaces/IRegistrosArtigosCategoria";
import IDadosArtigo from "../interfaces/IDadosArtigo";
import ILinkAmigavel from "../interfaces/ILinkAmigavel";
import ITema from "../interfaces/ITema";
import IApi, { IApiListagemResponse } from "../interfaces/IApi";
import Util from "./Util";
import { getPortalMode } from "../config/portalMode";

export default class CatalogoService {
    // Usamos um Map estático para armazenar todas as instâncias criadas.
    // A chave do Map será uma string única que representa a configuração da instância.
    private static instances: Map<string, CatalogoService> = new Map();

    // Propriedades da instância
    options: any = {};
    urlBasica: string;
    isAuthenticated: boolean = false;
    accessToken: string | undefined;

    /**
     * Obtém uma instância gerenciada do serviço a partir de um registro de instâncias.
     * Se uma instância com a mesma configuração já existir, ela é retornada.
     * Caso contrário, uma nova é criada e armazenada para uso futuro.
     * @param isAuthenticated Status de autenticação do usuário.
     * @param accessToken Token de acesso do usuário.
     * @param urlBasica A URL base da API, obtida via hook.
     * @returns A instância do CatalogoService correspondente à configuração.
     */
    public static getInstance(isAuthenticated: boolean, accessToken: string | undefined, urlBasica: string): CatalogoService {
        // Cria uma chave única baseada nos parâmetros que definem a instância.
        // Usamos "anon" para o token de usuários não autenticados para garantir uma chave consistente.
        const instanceKey = `${isAuthenticated}:${accessToken || 'anon'}:${urlBasica}`;

        // 1. Verifica se uma instância para esta chave já existe no Map.
        if (this.instances.has(instanceKey)) {
            console.log(`Retornando instância em cache para a chave: ${instanceKey}`);
            return this.instances.get(instanceKey)!; // O "!" afirma que o valor existe, pois has() retornou true.
        }

        // 2. Se não existir, cria uma nova instância.
        console.log(`Criando NOVA instância para a chave: ${instanceKey}`);
        const newInstance = new CatalogoService(isAuthenticated, accessToken, urlBasica);

        // 3. Armazena a nova instância no Map para futuras requisições.
        this.instances.set(instanceKey, newInstance);

        return newInstance;
    }

    /**
     * O construtor é privado para forçar o uso do método estático `getInstance`.
     */
    private constructor(isAuthenticated: boolean, accessToken: string | undefined, urlBasica: string) {
        this.isAuthenticated = isAuthenticated;
        this.accessToken = accessToken;
        this.urlBasica = urlBasica;

        if (this.isAuthenticated) {
            this.options = { headers: { "Authorization": `Bearer ${accessToken}` } };
        }
    }

    // ... O restante da sua classe permanece exatamente o mesmo ...
    // private updateAuthorizationHeader() { ... }
    // public async getComponentes(...) { ... }
    // etc.
    private updateAuthorizationHeader() {
        if(this.isAuthenticated) {
            this.options.headers = {"Authorization": `Bearer ${this.accessToken}`};
        }
    }

    private getQueryStringPage(page: number | undefined) {
        let queryStringPage = "";

        if (page) {
            queryStringPage = `&page=${page}`;
        }
        return queryStringPage;
    }

    private getModoQueryString() {
        return `modo=${encodeURIComponent(getPortalMode())}`;
    }

    private appendModoQueryString(url: string) {
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}${this.getModoQueryString()}`;
    }

    
    public async getComponentes(
        idsCategorias?: number[], 
        idsTecnologias?: number[], 
        idsTags?: number[], 
        idsTemas?: number[], 
        termo?: string,
        page?: number
    ): Promise<IComponente[]> {
        this.updateAuthorizationHeader();
        
        const filtroTecnologias = idsTecnologias?.join(':') || "";
        const filtroCategorias = idsCategorias?.join(':') || "";
        const filtroTags = idsTags?.join(':') || "";
        const filtroTemas = idsTemas?.join(':') || "";

        const queryStringPage = this.getQueryStringPage(page);

        const params = new URLSearchParams();
        if (filtroTecnologias) params.append('tecnologias', filtroTecnologias);
        if (filtroCategorias) params.append('categorias', filtroCategorias);
        if (filtroTags) params.append('tags', filtroTags);
        if (filtroTemas) params.append('temas', filtroTemas);
        if (termo?.trim()) params.append('termo', termo.trim());

        let url = `${this.urlBasica}v1/portal-conhecimento/custom/componentes-cards?${params.toString()}${queryStringPage}`;
        url = url.replace(/\?$/, '');
        url = this.appendModoQueryString(url);

        return fetch(url, this.options)
            .then(response => response.json() as Promise<IRegistros<IComponente>>)
            .then(data => data.registros)
            .catch(error => {
                console.error("Erro ao buscar componentes:", error);
                throw error;
            });
    }

    public async getComponenteCodigo(codigo: string) : Promise<IComponente> {
        this.updateAuthorizationHeader();

        const url = this.appendModoQueryString(`${this.urlBasica}v1/portal-conhecimento/custom/componentes?codigo=${codigo}`);
        console.log("URL para buscar componente:", url);
        console.log("Opções de requisição:", this.options);
        return fetch(url, this.options)
            .then(response => response.json() as Promise<IRegistros<IComponente>>)
            .then(data => {
                console.log("Dados recebidos:", data);
                const componente = data.registros[0];
                componente.sobre = Util.substituiUrlsImagens(componente.sobre);
                componente.comoComecar = Util.substituiUrlsImagens(componente.comoComecar);
                componente.documentacao = Util.substituiUrlsImagens(componente.documentacao);
                
                return componente;
            });
    }

    public async getTecnologias() : Promise<ITecnologia[]> {
        this.updateAuthorizationHeader();

        return fetch(this.appendModoQueryString(`${this.urlBasica}v1/portal-conhecimento/custom/tecnologias`), this.options)
            .then(response => response.json() as Promise<IRegistros<ITecnologia>>)
            .then(data => data.registros);
    }

    public async getCategorias() : Promise<ICategoria[]> {
        this.updateAuthorizationHeader();

        return fetch(this.appendModoQueryString(`${this.urlBasica}v1/portal-conhecimento/custom/categorias`), this.options)
            .then(response => response.json() as Promise<IRegistros<ICategoria>>)
            .then(data => data.registros);
    }

    public async getTags() : Promise<ITag[]> {
        this.updateAuthorizationHeader();

        return fetch(this.appendModoQueryString(`${this.urlBasica}v1/portal-conhecimento/custom/tags`), this.options)
            .then(response => response.json() as Promise<IRegistros<ITag>>)
            .then(data => data.registros);
    }

    public async busca(termo: string, pagina?: number) : Promise<IResultadosBusca> {
        this.updateAuthorizationHeader();
        const queryStringPage = this.getQueryStringPage(pagina);

        return fetch(this.appendModoQueryString(`${this.urlBasica}v1/portal-conhecimento/custom/busca?termo=${encodeURIComponent(termo)}${queryStringPage}`), this.options)
            .then(response => response.json() as Promise<IResultadosBusca>);
    }

    public async getDestaques() : Promise<IDestaque[]> {
        this.updateAuthorizationHeader();

        return fetch(this.appendModoQueryString(`${this.urlBasica}v1/portal-conhecimento/custom/destaques`), this.options)
            .then(response => response.json() as Promise<IRegistros<IDestaque>>)
            .then(data => data.registros);
    }

    public async getMaisAcessados() : Promise<IMaisAcessado[]> {
        this.updateAuthorizationHeader();

        return fetch(this.appendModoQueryString(`${this.urlBasica}v1/portal-conhecimento/custom/mais-acessados`), this.options)
            .then(response => response.json() as Promise<IRegistros<IMaisAcessado>>)
            .then(data => data.registros);
    }

    public async getHistoricoComponente(idComponente : number) : Promise<ILogComponente[]> {
        this.updateAuthorizationHeader();

        return fetch(this.appendModoQueryString(`${this.urlBasica}v1/portal-conhecimento/custom/versoes-componente?idComponente=${idComponente}`), this.options)
            .then(response => response.json() as Promise<IRegistros<ILogComponente>>)
            .then(data => data.registros);
    }

    public async getCategoriasDocumentacoes() : Promise<ICategoriaDocumentacao[]> {
        this.updateAuthorizationHeader();

        return fetch(this.appendModoQueryString(`${this.urlBasica}v1/portal-conhecimento/custom/categorias-documentacoes`), this.options)
            .then(response => response.json() as Promise<IRegistros<ICategoriaDocumentacao>>)
            .then(data => data.registros);
    }

    public async getRegistrosArtigosCategoria(codigoCategoria : String) : Promise<IRegistrosArtigosCategoria> {
        this.updateAuthorizationHeader();

        return fetch(this.appendModoQueryString(`${this.urlBasica}v1/portal-conhecimento/custom/artigos-documentacoes?codigoCategoria=${codigoCategoria}`), this.options)
            .then(response => response.json() as Promise<IRegistrosArtigosCategoria>);
    }

    public async getArtigo(codigoArtigo : String, codigoCategoria : String, url: String) : Promise<IDadosArtigo> {
        this.updateAuthorizationHeader();

        return fetch(this.appendModoQueryString(`${this.urlBasica}v1/portal-conhecimento/custom/artigo-documentacao?codigoArtigo=${codigoArtigo}&codigoCategoria=${codigoCategoria}&url=${url}`), this.options)
            .then(response => response.json() as Promise<IDadosArtigo>);
    }

    public async getLinksAmigaveisArtigos(idsArtigos : number[]) : Promise<ILinkAmigavel> {
        this.updateAuthorizationHeader();

        const options = {
            ...this.options,
            method: "POST",
            body: JSON.stringify(idsArtigos)
        };

        return fetch(this.appendModoQueryString(`${this.urlBasica}v1/portal-conhecimento/custom/urls-artigos-manuais`), options)
            .then(response => response.json() as Promise<ILinkAmigavel>);
    }

    public async getTemas() : Promise<ITema[]> {
        this.updateAuthorizationHeader();
        return fetch(this.appendModoQueryString(`${this.urlBasica}v1/portal-conhecimento/custom/temas`), this.options)
            .then(response => response.json() as Promise<IRegistros<ITema>>)
            .then(data => data.registros);
    }

    public async getAvatar(): Promise<string> {
        this.updateAuthorizationHeader();
        return fetch(this.appendModoQueryString(`${this.urlBasica}v1/portal-conhecimento/custom/avatar`), this.options)
            .then(response => response.json())
            .then(data => data.urlAvatar)
            .catch(error => {
                console.error("Erro ao buscar avatar:", error);
                throw error;
            });
    }

    public async getApisListagem(): Promise<IApi[]> {
        this.updateAuthorizationHeader();

        const url = this.appendModoQueryString(`${this.urlBasica}v1/portal-conhecimento/custom/apis-listagem`);
        return fetch(url, this.options)
            .then(response => response.json() as Promise<IApiListagemResponse>)
            .then(data => data.registros)
            .catch(error => {
                console.error("Erro ao buscar listagem de APIs:", error);
                throw error;
            });
    }

    public async getApi(idRotina: number): Promise<IApi> {
        this.updateAuthorizationHeader();

        const url = this.appendModoQueryString(`${this.urlBasica}v1/portal-conhecimento/custom/apis?idRotina=${idRotina}`);
        return fetch(url, this.options)
            .then(response => response.json() as Promise<IApi[]>)
            .then(data => {
                const api = data[0];
                if (api) {
                    api.descricao = Util.substituiUrlsImagens(api.descricao || "");
                    api.detalhamentoAcesso = Util.substituiUrlsImagens(api.detalhamentoAcesso || "");
                    api.detalhamentoFuncional = Util.substituiUrlsImagens(api.detalhamentoFuncional || "");
                }
                return api;
            })
            .catch(error => {
                console.error("Erro ao buscar detalhe da API:", error);
                throw error;
            });
    }
}